import React, { useEffect, useState } from 'react'
import { ModalContentProps } from '../utils/useModal'
import { RequestResponseTypes } from '../utils/Common/RequestResponseTypes'
import { queryServer } from '../utils/queryServer'
import { EotTextInList } from '../utils/EotTextInList'
import { EotPart } from '../utils/Common/Common'
import { EotPartAndRendered } from '../Contribute/SolveEot'
import * as _ from "lodash"
import { PlainTextPart } from '../Contribute/PlainTextPart'
import { LatexPart } from '../Contribute/LatexPart'
import { AddFieldCard } from '../Contribute/AddFieldCard'

type Params = {
    exerciseId: string
}

export const ParaphraseExerciseModal = ({
    params,
    closeModal,
}: ModalContentProps<Params>) => {
    const [startParaphrasingResponse, setStartParaphrasingResponse] = useState<
        RequestResponseTypes['startExerciseParaphrasing']['response']
    >()

    useEffect(() => {
        (async () => {
            const response = await queryServer(
                'startExerciseParaphrasing',
                { exerciseId: params.exerciseId },
            )

            setStartParaphrasingResponse(response)
        })()
    }, [])

    let content
    if (startParaphrasingResponse == null) {
        content = <h1>Please wait...</h1>
    } else if (startParaphrasingResponse.canParaphrase) {
        content = <>
            <RenderPartsWithEdit
                parts={startParaphrasingResponse.originExerciseText}
                type="origin"
                closeModal={closeModal}
                title={<span>Origin exercise:</span>}
                originExerciseId={params.exerciseId}
            />

            {startParaphrasingResponse.alreadyParaphrasedVersionText != null && <RenderPartsWithEdit
                parts={startParaphrasingResponse.alreadyParaphrasedVersionText}
                type="already_paraphrased"
                closeModal={closeModal}
                title={<span style={{ color: "red", fontWeight: "bold" }}>
                    Exercise is already paraphrased.
                    You can change current paraphrased version by paraphrase it again.
                </span>}
                originExerciseId={params.exerciseId}
            />}

            <RenderPartsWithEdit
                parts={startParaphrasingResponse.suggestedAiParaphrasedText}
                type="new_paraphrased"
                closeModal={closeModal}
                title={<span>New paraphrased exercise:</span>}
                originExerciseId={params.exerciseId}
            />
        </>
    } else {
        content = <>
            <h5>Can't paraphrase exercise</h5>
            <h5 style={{ color: "red" }}>
                {startParaphrasingResponse.reason}
            </h5>
            <hr className='mg-t-30 mg-b-30' style={{ borderColor: 'red', borderWidth: 3 }} />
            {renderEotParts(
                startParaphrasingResponse.originExerciseText,
                <span>Origin exercise:</span>,
                () => { },
            )}
        </>
    }

    return <div className="save-exercise-modal responsive-modal-box mg-30">
        {content}
    </div>
}

const RenderPartsWithEdit = ({
    originExerciseId,
    parts,
    type,
    closeModal,
    title,
}: {
    originExerciseId: string
    parts: EotPart[]
    type: 'new_paraphrased' | 'origin' | 'already_paraphrased'
    closeModal: () => void
    title: JSX.Element
}) => {
    const editable = type === 'new_paraphrased'

    const [eotText, setEotText] = React.useState<EotPartAndRendered[]>(parts.map(it => ({
        ...it,
        renderRaw: true,
    })))

    const setEotTextX: React.Dispatch<React.SetStateAction<EotPartAndRendered[]>> = (value) => {
        if (editable) {
            setEotText(value)
        }
    }

    const [show, setShow] = useState(false)

    const deletePartId = React.useRef<string | null>(null)
    const deleteTimerRef = React.useRef<NodeJS.Timeout>()
    const deletePartHandler = (partId: string) => {
        if (deleteTimerRef.current) {
            clearTimeout(deleteTimerRef.current)
        }

        if (deletePartId.current === partId) {
            setEotTextX(prev => {
                return prev.filter(it => it.id !== partId)
            })

            deletePartId.current = null
        } else {
            deletePartId.current = partId
            deleteTimerRef.current = setTimeout(
                () => deletePartId.current = null,
                800,
            )
        }
    }

    const toggleAllParts = () => {
        let newRenderRaw: boolean
        if (eotText.length > 0) {
            newRenderRaw = !eotText[0].renderRaw
        } else {
            newRenderRaw = true
        }

        setEotTextX(eotText.map(it => ({ ...it, renderRaw: newRenderRaw })))
    }

    const togglePartHandler = (id: string) => {
        setEotTextX(eotText.map(it => ({
            ...it,
            renderRaw: it.id === id ? !it.renderRaw : it.renderRaw,
        })))
    }

    const latexBody = <>
        {editable && <div className="contributors-page-heder mg-t-10">
            <button
                className="btn btn-warning btn-teal mg-b-10"
                style={{ backgroundColor: '#9900e6' }}
                onClick={toggleAllParts}
            >Toggle all parts</button>
        </div>}
        <div className="row exercise-page">
            {_.sortBy(eotText, (eotPartAndRendered) => eotPartAndRendered.order_number)
                .map(it => {
                    if (it.renderRaw) {
                        return <PlainTextPart
                            key={it.id}
                            togglePart={() => togglePartHandler(it.id)}
                            deletePart={() => deletePartHandler(it.id)}
                            eotPart={it}
                            setEotParts={setEotTextX}
                        />
                    } else {
                        return <LatexPart
                            key={it.id}
                            eotPart={it}
                            toggle={() => togglePartHandler(it.id)}
                            deletePart={() => deletePartHandler(it.id)}
                        />
                    }
                })}

            {editable && <AddFieldCard setNewParts={setEotTextX} />}
        </div>
    </>

    return <>
        <hr className='mg-t-30 mg-b-30' style={{ borderColor: '#32a852', borderWidth: 20 }} />
        {renderEotParts(
            eotText,
            title,
            () => setShow(!show),
        )}
        <div className='contributors-exercise-page'>
            {/* <span
                style={{ background: "lightgray", color: "black", borderColor: "black", fontSize: '70%' }}
                className='span-btn'
                onClick={() => setShow(!show)}
            >
                {show ? "Hide LaTeX" : "Show LaTeX"}
            </span> */}
            {show && latexBody}

            {editable && <div className='mg-t-50'>
                <hr className='mg-t-30 mg-b-30' style={{ borderColor: '#32a852', borderWidth: 20 }} />
                <span
                    className='span-btn'
                    style={{ backgroundColor: "orange", color: "black", borderColor: "black" }}
                    onClick={async () => {
                        const response = await queryServer(
                            'finishExerciseParaphrasing',
                            {
                                originExerciseId: originExerciseId,
                                paraphrasedExerciseText: eotText,
                            }
                        )

                        console.log(response)

                        closeModal()
                    }}
                >Save paraphrased exercise</span>
            </div>}
        </div>
    </>
}

function renderEotParts(
    eotParts: EotPart[],
    title: JSX.Element,
    onClick: () => void,
) {
    return <div className='mg-b-30'>
        {title}
        <EotTextInList
            eotText={eotParts}
            onClick={onClick}
            timestamp={undefined}
            difficulty={undefined}
            area={undefined}
            exerciseOrderNumberInList={undefined}
            avgOrderNumInBook={undefined}
        />
    </div>
}
